import React from 'react';
import { isBlank, isPresent } from 'utils/helperMethods';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { isNil } from 'ramda';
import { Typography, Avatar } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import BidPresenter from 'presenters/BidPresenter';

import { Skeleton } from '@material-ui/lab';

import MessagePresenter from 'presenters/MessagePresenter';
import LoanOfficerPresenter from 'presenters/LoanOfficerPresenter';
import LastMessage from './components/LastMessage';

import useStyles from './useStyles';

const LoanOfficerCard = props => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    isOnline,
    lastEventAt,
    onClick,
    isHightlighted,
    unreadCount,
    bid,
    loanOfficer,
    latestMessage,
    loanOfficerId,
  } = props;

  const isLoanOfficerPresent = isPresent(loanOfficer);
  const loanOfficerName = isLoanOfficerPresent
    ? LoanOfficerPresenter.fullName(loanOfficer)
    : t('models.App.userTypes.loanOfficer');
  const isCompanyLogoPresent = isLoanOfficerPresent && isPresent(LoanOfficerPresenter.companyLogoUrl(loanOfficer));
  const imageUrl = isLoanOfficerPresent ? LoanOfficerPresenter.profileImageUrl(loanOfficer) : '';
  const avatarLabel = isLoanOfficerPresent ? LoanOfficerPresenter.initials(loanOfficer) : '';
  const companyName = isLoanOfficerPresent ? LoanOfficerPresenter.companyName(loanOfficer) : '';

  const hasUnreadMessages = !isNil(unreadCount) && unreadCount !== 0;

  const apr = isBlank(bid) ? '--' : BidPresenter.getApr(bid);
  const interestRate = isBlank(bid) ? '--' : BidPresenter.getInterestRate(bid);
  const payment = isBlank(bid) ? '--' : BidPresenter.monthlyPrInMi(bid);
  const isOutsideGuidelines = BidPresenter.isOutsideGuidelines(bid);

  return (
    <div
      className={clsx(classes.container, { [classes.active]: isHightlighted, [classes.inActive]: !isHightlighted })}
      onClick={onClick}
      role="presentation"
    >
      <div className={classes.loanOfficerInfo}>
        <div className={classes.avatarWithBadge}>
          <Avatar src={imageUrl} label={avatarLabel} className={classes.avatar} />
          {hasUnreadMessages && (
            <div className={classes.unreadMessagesBadge}>
              <Typography variant="subtitle1" className={classes.unreadMessagesText}>
                {`${unreadCount}`}
              </Typography>
            </div>
          )}
        </div>
        <div className={classes.info}>
          <div className={classes.infoItem}>
            {isLoanOfficerPresent ? (
              <Typography variant="h2" noWrap className={classes.name}>
                {loanOfficerName}
              </Typography>
            ) : (
              <Skeleton width={150} height={30} />
            )}
            {isOnline && (
              <Typography variant="subtitle1" color="inherit" className={classes.onineStatus}>
                {t('components.LoanOfficerCard.online')}
              </Typography>
            )}
          </div>
          {isLoanOfficerPresent ? (
            <Typography variant="subtitle1" className={classes.companyName}>
              {companyName}
            </Typography>
          ) : (
            <Skeleton variant="text" />
          )}
          <Typography variant="subtitle1" color="secondary" className={classes.date}>
            {isBlank(lastEventAt) ? t('components.LoanOfficerCard.away') : lastEventAt}
          </Typography>
        </div>
        <div className={classes.companyLogotype}>
          {isCompanyLogoPresent && (
            <img
              alt="company logo"
              className={classes.companyLogo}
              src={LoanOfficerPresenter.companyLogoUrl(loanOfficer)}
            />
          )}
        </div>
      </div>
      <LastMessage latestMessage={latestMessage} loanOfficerId={loanOfficerId} loanOfficerName={loanOfficerName} />
      <div className={classes.bottomBar}>
        {isOutsideGuidelines ? (
          <div className={classes.bottomBarFull}>
            <Typography className={classes.bidInfoText}>
              {t('components.LoanOfficerCard.outsideGuidelines', { name: loanOfficerName.split(' ')[0] })}
            </Typography>
          </div>
        ) : (
          <React.Fragment>
            <div className={classes.bottomBarLeft}>
              <Typography variant="subtitle1" color="secondary">
                {t('components.LoanOfficerCard.interestRate')}
              </Typography>
              <Typography variant="h2" className={classes.bidInfoText}>
                {interestRate}
              </Typography>
            </div>
            <div className={classes.bottomBarCenter}>
              <Typography variant="subtitle1" color="secondary">
                {t('components.LoanOfficerCard.apr')}
              </Typography>
              <Typography variant="h2" className={classes.bidInfoText}>
                {apr}
              </Typography>
            </div>
            <div className={classes.bottomBarRight}>
              <Typography variant="subtitle1" color="secondary">
                {t('components.LoanOfficerCard.payment')}
              </Typography>
              <Typography variant="h2" className={classes.bidInfoText}>
                {payment}
              </Typography>
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

LoanOfficerCard.propTypes = {
  isOnline: PropTypes.bool.isRequired,
  isHightlighted: PropTypes.bool,
  lastEventAt: PropTypes.string,
  latestMessage: MessagePresenter.shape(),
  unreadCount: PropTypes.number,
  onClick: PropTypes.func.isRequired,
  bid: BidPresenter.shape(),
  loanOfficerId: PropTypes.number.isRequired,
  loanOfficer: LoanOfficerPresenter.shape(),
};

LoanOfficerCard.defaultProps = {
  lastEventAt: null,
  unreadCount: 0,
  isHightlighted: false,
  latestMessage: null,
  loanOfficer: {},
  bid: {},
};

export default LoanOfficerCard;



// WEBPACK FOOTER //
// ./src/sharedComponents/LoanOfficerCard/LoanOfficerCard.js